import { PEOPLE_LIMIT } from '../config/limits'
import { MOVED_KEY, oldKey, spaceKey } from './space'
import { ENABLED_LOCALES, type Locale } from '../i18n/locales'
import { CARD_COUNT } from './tarot'
import { FAMILY_KINDS, isLoveKind, isRelationKind, LOVE_KINDS, type RelationKind } from './features/relations'
import type { DrawnCard, Direction } from './tarotTopic'
import { randomBytes, toHex } from './hash'

/**
 * 로그인하지 않은 사용자의 기기(localStorage) 저장: 저장한 사람, 카드 도감, 내 결과 목록.
 *
 * 로그인하지 않으면 다른 사람의 출생 정보는 **서버에 보내지 않는다.**
 * 로그인하면 이 값들을 계정으로 옮기고 기기 사본은 지운다 (src/auth/store.ts).
 *
 * 저장 형식이 바뀌면 키의 v 숫자를 올린다. 읽을 때 형식이 이상하면 빈 값으로 시작한다.
 *
 * **4단계부터 열쇠 이름에 언어가 들어간다** (`saju.ko.results.v1`). 로그인하지 않은
 * 사람에게는 이것이 곧 언어 공간이다 — 한국어에서 만든 결과는 영어 화면에 보이지 않고,
 * 다시 한국어로 오면 그대로 있다. 옛 열쇠(`saju.results.v1`)는 **처음 한 번** 지금
 * 언어로 옮기고 표시를 남긴다 (`moveOldKeys`).
 */

/**
 * 지금 공간의 언어. 화면이 시작할 때와 언어를 바꿀 때 세운다 (`setSpaceLocale`).
 * 세우기 전에는 옛 열쇠 이름을 그대로 쓴다 — 첫 렌더 전에 세우므로 실제로는 거의 없다.
 */
let current = ''

/** 지금 언어 공간을 세운다. 세우면서 옛 열쇠를 한 번 옮긴다. */
export function setSpaceLocale(locale: string): void {
  current = locale
  moveOldKeys(locale)
}

/** 지금 공간의 열쇠 이름 */
function key(name: string): string {
  return current ? spaceKey(current, name) : oldKey(name)
}

/** 옮길 것들 */
const NAMES = ['people', 'collection', 'results', 'talismans', 'spread', 'invites'] as const

/**
 * **옛 열쇠를 지금 언어 공간으로 한 번 옮긴다.**
 *
 * 언어가 붙기 전에 만든 것은 어느 공간의 것인지 알 길이 없다. 그래서 **처음 여는
 * 언어**의 공간으로 넣는다 (그 사람이 쓰던 언어다). 옮긴 뒤 옛 열쇠를 지우고
 * 표시를 남겨 두 번 옮기지 않는다 — 두 번 옮기면 나중 언어로 딸려 간다.
 */
export function moveOldKeys(locale: string): void {
  try {
    if (window.localStorage.getItem(MOVED_KEY)) return
    let moved = 0
    for (const name of NAMES) {
      const from = window.localStorage.getItem(oldKey(name))
      if (from === null) continue
      // 그 언어에 이미 값이 있으면 덮지 않는다
      if (window.localStorage.getItem(spaceKey(locale, name)) === null) {
        window.localStorage.setItem(spaceKey(locale, name), from)
        moved += 1
      }
      window.localStorage.removeItem(oldKey(name))
    }
    window.localStorage.setItem(MOVED_KEY, `${locale}:${moved}`)
  } catch {
    // 저장소를 못 쓰면 옮길 것도 없다.
  }
}

function read<T>(key: string, fallback: T, isValid: (v: unknown) => v is T): T {
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return fallback
    const parsed: unknown = JSON.parse(raw)
    return isValid(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

// 저장한 사람

const PEOPLE_KEY = () => key('people')

export const PERSON_RELATIONS = ['partner', 'family', 'friend', 'other'] as const

/**
 * 예전에 저장한 사람의 거친 관계(네 가지)를 자세한 사이로 옮긴다.
 * 관계 종류를 열여섯으로 늘리면서, 이미 저장된 사람이 깨지지 않게 두는 길이다.
 *   연인·배우자 → 연인 / 가족 → 부모 / 친구 → 친구 / 기타 → 기타
 */
export function defaultKind(relation: PersonRelation): RelationKind {
  if (relation === 'partner') return 'lover'
  if (relation === 'family') return 'parent'
  if (relation === 'friend') return 'friend'
  return 'other'
}

/** 저장된 사람의 사이. 예전 항목은 거친 관계에서 옮겨 온다. */
export function kindOf(person: Pick<Person, 'relation' | 'kind'>): RelationKind {
  return person.kind && isRelationKind(person.kind) ? person.kind : defaultKind(person.relation)
}

/** 자세한 사이에 맞는 거친 관계 (예전 형식과 함께 저장해 둔다) */
export function relationOfKind(kind: RelationKind): PersonRelation {
  if (LOVE_KINDS.includes(kind as (typeof LOVE_KINDS)[number])) return 'partner'
  if (FAMILY_KINDS.includes(kind as (typeof FAMILY_KINDS)[number])) return 'family'
  if (kind === 'friend') return 'friend'
  return 'other'
}
export type PersonRelation = (typeof PERSON_RELATIONS)[number]

/**
 * 궁합 목록을 나누는 두 묶음.
 *  - `love` 연애·배우자 (연인·배우자·짝사랑·헤어진 사람) — 자세한 연애 궁합을 보는 사이
 *  - `family` 가족 (부모·자녀·형제자매·배우자의 가족) — : 부모를 "그 외 사람" 에 넣고 있었다
 *  - `other` 그 외 사람 (일·친구·그 밖)
 * 화면을 가르지 않고 **목록만** 가른다. 한쪽이 비면 그 제목은 그리지 않는다.
 */
export type PeopleGroupKey = 'love' | 'family' | 'other'

/** 이 사람이 어느 묶음인가. 판정은 자세한 사이(kind) 기준이다. */
export function peopleGroupOf(person: Pick<Person, 'relation' | 'kind'>): PeopleGroupKey {
  const kind = kindOf(person)
  if (isLoveKind(kind)) return 'love'
  return FAMILY_KINDS.includes(kind as (typeof FAMILY_KINDS)[number]) ? 'family' : 'other'
}

/** 목록을 두 묶음으로 가른다. 각 묶음 안의 차례는 원래 차례 그대로다. */
export function splitPeople<T extends Pick<Person, 'relation' | 'kind'>>(people: readonly T[]): { love: T[]; family: T[]; other: T[] } {
  const out = { love: [] as T[], family: [] as T[], other: [] as T[] }
  for (const person of people) out[peopleGroupOf(person)].push(person)
  return out
}

export interface Person {
  id: string
  /** 이름이나 별명. 기기에만 있고 서버·AI 로 보내지 않는다. */
  nickname: string
  relation: PersonRelation
  /** 관계별 궁합에 쓰는 자세한 사이 (없으면 relation 에서 짐작한다) */
  kind?: RelationKind
  birthDate: string
  birthTime?: string
  hourUnknown: boolean
  cityId: string
  sex: 'female' | 'male'
  createdAt: string
  /**
   * 이 사람의 궁합을 마지막으로 연 날 (YYYY-MM-DD, 기기 현지).
   * 예전에 저장한 사람에게는 없다 — 그때는 "아직 안 봤어요" 로 보여 준다.
   */
  lastSeenAt?: string
  /**
   * 이 사람을 **어느 사주에서** 담았나 (사주마다 따로). 결과 번호.
   * 예전에 담은 사람에게는 없다 — 그때는 "나" 사주(기기에서 가장 먼저 만든 결과) 밑에 보인다.
   */
  resultId?: string
}

/** 로그인 전(기기) 사람 한도 — : 30 → 100 (`src/config/limits.ts`) */
export const MAX_PEOPLE = PEOPLE_LIMIT
export const MAX_NICKNAME = 30

function isPerson(v: unknown): v is Person {
  const p = v as Person
  return (
    !!p &&
    typeof p.id === 'string' &&
    typeof p.nickname === 'string' &&
    (PERSON_RELATIONS as readonly string[]).includes(p.relation) &&
    typeof p.birthDate === 'string' &&
    typeof p.hourUnknown === 'boolean' &&
    typeof p.cityId === 'string' &&
    (p.sex === 'female' || p.sex === 'male') &&
    (p.kind === undefined || isRelationKind(p.kind)) &&
    (p.resultId === undefined || typeof p.resultId === 'string')
  )
}

function isPeople(v: unknown): v is Person[] {
  return Array.isArray(v) && v.every(isPerson)
}

export function loadPeople(): Person[] {
  return read(PEOPLE_KEY(), [], isPeople)
}

/**
 * **이 기기에 담긴 사람 — 모든 언어를 합쳐.** 한도도 이 수로 본다
 * (로그인하면 계정 전체를 DB 가 센다 — `my_people_limit`). 예전에는 지금 언어의 목록만 세어 언어마다 100명씩 담겼다.
 */
export function countDevicePeople(): number {
  // 언어 공간을 세우기 전(옛 열쇠 `saju.people.v1`)이면 그 목록도 함께 — 지금 쓰는 열쇠가 언어 열쇠 가운데 하나면 한 번만 센다
  const keys = new Set([...ENABLED_LOCALES.map((locale) => spaceKey(locale, 'people')), PEOPLE_KEY()])
  return [...keys].reduce((n, k) => n + read(k, [] as Person[], isPeople).length, 0)
}

export function savePeople(people: Person[]): boolean {
  return write(PEOPLE_KEY(), people.slice(0, MAX_PEOPLE))
}

export function newPersonId(): string {
  return toHex(randomBytes(8))
}

// 사람 목록 조작은 순수 함수로 둔다 (테스트하기 쉽게).
export function addPerson(people: Person[], person: Person): Person[] {
  if (people.length >= MAX_PEOPLE) return people
  return [...people, { ...person, nickname: person.nickname.trim().slice(0, MAX_NICKNAME) }]
}

export function removePerson(people: Person[], id: string): Person[] {
  return people.filter((p) => p.id !== id)
}

// 카드 도감

const COLLECTION_KEY = () => key('collection')

export interface Collection {
  /** 카드 id → 방향별 **처음 본 날짜** */
  cards: Record<string, Partial<Record<Direction, string>>>
  /**
   * 예전에 "오늘의 카드" 를 하루 한 장만 넣으려고 쓰던 날짜.
   * 그 기능을 없애서 이제 쓰지 않는다 — 예전에 저장된 값을 버리지 않으려고 자리만 둔다.
   */
  lastDailyDate?: string
}

export const EMPTY_COLLECTION: Collection = { cards: {} }

function isCollection(v: unknown): v is Collection {
  const c = v as Collection
  return !!c && typeof c.cards === 'object' && c.cards !== null && !Array.isArray(c.cards)
}

export function loadCollection(): Collection {
  return read(COLLECTION_KEY(), EMPTY_COLLECTION, isCollection)
}

export function saveCollection(collection: Collection): boolean {
  return write(COLLECTION_KEY(), collection)
}

/**
 * 펼쳐서 본 카드를 도감에 표시한다 — 모으는 게임이 아니라 **"본 적 있음" 표시**다.
 * 처음 본 날짜는 덮어쓰지 않는다.
 */
export function addToCollection(collection: Collection, cards: DrawnCard[], today: string): Collection {
  const next: Collection = { ...collection, cards: { ...collection.cards } }
  for (const card of cards) {
    const key = String(card.id)
    const current = next.cards[key] ?? {}
    if (current[card.direction]) continue
    next.cards[key] = { ...current, [card.direction]: today }
  }
  return next
}

export interface CollectionSummary {
  /** 한 방향이라도 본 카드 수 */
  collected: number
  /** 정·역방향을 둘 다 본 카드 수 */
  completed: number
  total: number
}

export function summarizeCollection(collection: Collection): CollectionSummary {
  let collected = 0
  let completed = 0
  for (let id = 0; id < CARD_COUNT; id += 1) {
    const c = collection.cards[String(id)]
    if (!c) continue
    if (c.upright || c.reversed) collected += 1
    if (c.upright && c.reversed) completed += 1
  }
  return { collected, completed, total: CARD_COUNT }
}

// 내 결과 목록 (같은 기기에서 "내 결과 이어보기")

// 켈틱 크로스 한 판 (결과마다 하나. 다시 열면 같은 패가 나온다)

const SPREAD_KEY = () => key('spread')

export interface StoredSpread {
  seed: string
  topic: string
  question: string
  summary?: string
}

/**
 * 결과 id 별로 마지막 판을 기억한다.
 * **씨앗만 저장하면 같은 패가 그대로 다시 펼쳐진다** (카드 열 장을 저장할 필요가 없다).
 * 새로 펼치면 씨앗이 바뀌므로 새 카드가 나온다 — 타로는 그래야 한다.
 */
function isSpreadMap(v: unknown): v is Record<string, StoredSpread> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

export function saveSpread(resultId: string, spread: StoredSpread): boolean {
  const all = read(SPREAD_KEY(), {} as Record<string, StoredSpread>, isSpreadMap)
  return write(SPREAD_KEY(), { ...all, [resultId]: spread })
}

const RESULTS_KEY = () => key('results')
export const MAX_RESULTS = 50

export interface ResultEntry {
  /** /r/{id} 의 id */
  id: string
  /** 저장한 날짜 YYYY-MM-DD (기기 현지) */
  savedOn: string
  /** 목록에 보여줄 일주 두 글자. 이름·생일은 두지 않는다. */
  dayPillar: string
  /**
   * **띠 지지 한 글자** (`酉`) —.
   *
   * 목록에 보이는 동물은 **본인 띠 하나**다. 생일 전체는 여기 담지 않는다 (원래 규칙
   * 그대로) — 지지 한 글자로는 태어난 해도 알 수 없다 (열두 해마다 돌아온다).
   * 옛 항목에는 없다. **그 결과를 열 때 채운다** (`App` 의 결과 불러오기).
   */
  zodiac?: string
  /** 입력 서명 (src/lib/resultSignature.ts). 같은 입력이면 같은 값. 예전 항목에는 없다. */
  sig?: string
  /**
   * 지우기 열쇠. 결과를 만들 때 서버가 한 번만 돌려준 값이다 (16진수 32글자).
   * **이 값이 있어야** 그 결과를 지우거나 타로 질문을 읽을 수 있다.
   * 예전 항목에는 없다 — 그런 결과는 계정에 저장해야 손댈 수 있다.
   */
  key?: string
}

function isResultList(v: unknown): v is ResultEntry[] {
  return (
    Array.isArray(v) &&
    v.every(
      (e) =>
        e &&
        typeof e.id === 'string' &&
        typeof e.savedOn === 'string' &&
        typeof e.dayPillar === 'string' &&
        (e.zodiac === undefined || typeof e.zodiac === 'string') &&
        (e.sig === undefined || typeof e.sig === 'string') &&
        (e.key === undefined || typeof e.key === 'string'),
    )
  )
}

export function loadResultList(): ResultEntry[] {
  return read(RESULTS_KEY(), [], isResultList)
}

/**
 * **언어마다 담긴 결과 목록 전부**.
 *
 * 기기 저장소는 열쇠 이름에 언어가 들어가 이미 공간별로 갈려 있다
 * (`saju.{언어}.results.v1`). 로그인할 때 이 기기 것을 계정으로 옮기려면
 * **지금 보는 언어뿐 아니라 모든 언어**를 읽어야 한다.
 */
export function loadResultListsByLocale(): { locale: Locale; entries: ResultEntry[] }[] {
  const out: { locale: Locale; entries: ResultEntry[] }[] = []
  for (const locale of ENABLED_LOCALES) {
    const entries = read(spaceKey(locale, 'results'), [] as ResultEntry[], isResultList)
    if (entries.length > 0) out.push({ locale, entries })
  }
  return out
}

/** 이 기기가 가진 그 결과의 지우기 열쇠. 없으면 null. */
export function resultKeyOf(id: string): string | null {
  // 지금 언어만 보면 다른 언어로 만든 결과의 열쇠를 못 찾는다 — 모든 언어 목록을 본다
  for (const { entries } of loadResultListsByLocale()) {
    const key = entries.find((e) => e.id === id)?.key
    if (key) return key
  }
  return resultKeys()[id] ?? null
}

/**
 * **이 기기에서 만든 결과의 지우기 열쇠**. 결과 주소가 주인에게만 열리면서, 로그인한 채
 * 만든 결과도 이 기기에서 주인임을 보일 길이 필요해졌다 — 로그인한 동안은 기기 목록에 쓰지 않아
 * 열쇠가 남지 않았고, 계정 목록에 담기기(비동기) 전에 풀이를 부르면 주인이 아니라고 막혔다.
 * 언어 공간과 무관하게 한 곳에 둔다 (열쇠는 결과마다 하나다).
 */
const RESULT_KEYS = 'saju.resultKeys.v1'
const KEY_RE = /^[0-9a-f]{32}$/

function resultKeys(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(RESULT_KEYS)
    const parsed = raw ? (JSON.parse(raw) as unknown) : {}
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

export function rememberResultKey(id: string, key: string | undefined): void {
  if (!key || !KEY_RE.test(key)) return
  try {
    const all = resultKeys()
    all[id] = key
    // 오래된 것부터 버려 100개까지만 둔다
    const entries = Object.entries(all).slice(-100)
    window.localStorage.setItem(RESULT_KEYS, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    // 기기 저장소가 막혀 있으면 계정 쪽으로만 주인임을 보인다
  }
}

/** 기기 목록에서 결과 하나를 뺀다 (서버에서 지운 뒤에 부른다). */
export function forgetResult(id: string): void {
  saveResultList(loadResultList().filter((e) => e.id !== id))
}

export function saveResultList(list: ResultEntry[]): boolean {
  return write(RESULTS_KEY(), list.slice(0, MAX_RESULTS))
}

/** 같은 id·같은 입력 서명은 한 번만, 최근 것이 맨 앞 */
export function rememberResult(list: ResultEntry[], entry: ResultEntry): ResultEntry[] {
  return [entry, ...list.filter((e) => e.id !== entry.id && !(entry.sig && e.sig === entry.sig))].slice(0, MAX_RESULTS)
}

export function mergeResultLists(a: ResultEntry[], b: ResultEntry[]): ResultEntry[] {
  const byId = new Map<string, ResultEntry>()
  for (const e of [...a, ...b]) if (!byId.has(e.id)) byId.set(e.id, e)
  return [...byId.values()].sort((x, y) => (x.savedOn < y.savedOn ? 1 : x.savedOn > y.savedOn ? -1 : 0)).slice(0, MAX_RESULTS)
}

/** 계정 삭제 뒤, 또는 계정으로 옮긴 뒤 기기 사본을 지운다. (TALISMANS_KEY 는 아래에 선언돼 있지만 호출 시점에는 정의돼 있다) */
export function clearDeviceAccountData(): void {
  try {
    for (const k of [PEOPLE_KEY(), COLLECTION_KEY(), RESULTS_KEY(), TALISMANS_KEY(), SPREAD_KEY()]) window.localStorage.removeItem(k)
  } catch {
    // 저장소를 못 쓰는 환경이면 지울 것도 없다.
  }
}

// 내 부적함 (이미지가 아니라 다시 그릴 값만)

const TALISMANS_KEY = () => key('talismans')
export const MAX_TALISMANS = 50

export interface TalismanEntry {
  id: string
  element: 'wood' | 'fire' | 'earth' | 'metal' | 'water'
  seed: number
  savedOn: string
  /**
   * 무엇에 쓰려고 담았는지 (재물·연애·합격·일·컨디션·전체 운).
   * **없을 수 있다** — 이 칸을 만들기 전에 담은 부적이 있다. 그때는 시드에서
   * 되찾아 보고(`purposeOf`), 그것도 안 되면 "용도 없음" 으로 보여 준다.
   */
  purpose?: string
  /**
   * 그 부적이 **어느 달**의 것인가 (`2026-10` 꼴). 25일이 지나면 다음 달
   * 부적을 미리 만들 수 있어, 담은 날과 부적의 달이 다를 수 있다.
   * **없을 수 있다** — 이 칸을 만들기 전에 담은 부적은 담은 날로 묶는다.
   */
  month?: string
  /**
   * **담을 때의 그림 버전**. 그림 셈을 고쳐도 담긴 부적이 그대로 그려진다.
   * **없을 수 있다** — 이 칸을 만들기 전에 담은 줄은 `TALISMAN_DRAW_LEGACY`(2) 로 본다.
   */
  drawVersion?: number
  /** 어느 사주의 보관함인가. 옛 줄은 없다 — "나" 사주 밑에 보인다 */
  resultId?: string
}

function isTalismanList(v: unknown): v is TalismanEntry[] {
  return (
    Array.isArray(v) &&
    v.every(
      (e) =>
        e &&
        typeof e.id === 'string' &&
        ['wood', 'fire', 'earth', 'metal', 'water'].includes(e.element) &&
        Number.isInteger(e.seed) &&
        typeof e.savedOn === 'string' &&
        (e.purpose === undefined || typeof e.purpose === 'string') &&
        (e.month === undefined || typeof e.month === 'string') &&
        (e.drawVersion === undefined || Number.isInteger(e.drawVersion)) &&
        (e.resultId === undefined || typeof e.resultId === 'string'),
    )
  )
}

export function loadTalismans(): TalismanEntry[] {
  return read(TALISMANS_KEY(), [], isTalismanList)
}

export function saveTalismans(list: TalismanEntry[]): boolean {
  return write(TALISMANS_KEY(), list.slice(0, MAX_TALISMANS))
}
